/** @license @dairyfarm/eval-web v3.0.0
* Copyright 2020 Texas A&M Center for Applied Technology, 
* Texas A&M Engineering Experiment Station, The Texas A&M 
* University System.
* 
* This source code/schema and any derivative work is Texas 
* A&M Engineering Experiment Station intellectual property 
* and protected by copyright law. Usage is restricted solely 
* to the FARM Animal Care and Environmental Stewardship 
* programs administered by the National Milk Producers 
* Federation. Dissemination of this source code and/or 
* derivative work creation is strictly prohibited without 
* express written permission from the Texas A&M Engineering 
* Experiment Station.
*/
import { Button, Container, List, Theme, Typography, Card, CardHeader, CardActions, CardContent } from "@material-ui/core";
import { createStyles, makeStyles } from "@material-ui/styles";
import React, { FC, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { AppState, loadEvaluations, syncSystem } from "../../redux";
import { selectEvals } from "../hooks";
import EvalListItem from "./EvalListItem";
import FollowUpFacilities from "./FollowUpFacilities";
import NewEvalButton from "./NewEvalButton";
import KeyboardArrowRightIcon from '@material-ui/icons/KeyboardArrowRight';
import LoadingIndicator from './form/components/LoadingIndicator';

interface HomeCardProps {
  title: string;
  children: React.ReactNode;
  link: string;
}

const HomeCard: FC<HomeCardProps> = props => {
  const classes = useStyles(props);
  const { title, children, link } = props;

  return (
    <Card className={classes.card}>
      {title && (
        <CardHeader className={classes.cardHeader} title={title} titleTypographyProps={{variant:'h6'}}></CardHeader>
      )}
      {/* <List className={classes.cardContents}>{children}</List> */}
      {children}

      <CardActions disableSpacing className={classes.cardFooter}>
        <Link to={link} className={classes.link}>
          <Button color="primary" size="small" endIcon={<KeyboardArrowRightIcon />}>View All</Button>
        </Link>
      </CardActions>
    </Card>
  );
};

interface HomeProps { }

const Home: FC<HomeProps> = props => {
  const classes = useStyles(props);
  const evals = useSelector(selectEvals);
  const dispatch = useDispatch();

  useEffect(function onMount() {
    dispatch(syncSystem());
  }, [dispatch]);

  const loading = useSelector((state: AppState) => state.evals.loading);
  const fetchingEvals = useSelector((state: AppState) => state.evals.isFetching);

  useEffect(() => {
    if (!fetchingEvals) {
      dispatch(loadEvaluations(true, 5));
    }
  }, [fetchingEvals, dispatch])

  return (
    <div className={classes.root}>
      <div className={classes.header}>
        {/* {newItems && (    // Disabled because new item determination is not well defined as a protocol
          <div className={classes.banner}>
            <Typography variant="body2">
              This list may be stale. Refresh to view most recently edited evaluations.
            </Typography>
          </div>
        )} */}
        <NewEvalButton />
      </div>

      <div className={classes.main}>
        <Container maxWidth="md" className={classes.container}>
          <HomeCard title="Recent Evaluations" link="/evals">
            <CardContent className={classes.cardContents}>
              <List>
                {(fetchingEvals || loading) ? (<LoadingIndicator classes={{
                  container: classes.loading
                }}>Loading Evals</LoadingIndicator>) :
                
                  evals.slice(0, 5).map((
                    evlId // Only display a handful of evals on this page
                  ) => (
                      <EvalListItem evalID={evlId} key={evlId} />
                    ))}
              </List>
            </CardContent>
          </HomeCard>
          {/* todo just use small list, like in the other component */}
          <HomeCard title="Action Plans Due within 30 Days" link="/follow-ups">
            <CardContent className={classes.cardContents}>
              <FollowUpFacilities
                limit={4}
                preview={true}
                // @ts-ignore This gets passed through
                classes={{
                  root: classes.facilitiesRoot,
                  list: classes.facilitiesList,
                }}
              />
            </CardContent>
          </HomeCard>
        </Container>
      </div>
    </div>
  );
};

/** todo separate fab from header */
const useStyles = makeStyles(({ breakpoints, spacing, palette, shape, shadows }: Theme) =>
  createStyles({
    root: {
      width: "100%",
      overflowY: "auto",
      WebkitOverflowScrolling: "touch",
      [breakpoints.down("sm")]: {
        paddingBottom: spacing(20)
      }
    },
    header: {
      display: "flex",
      justifyContent: "flex-end",
      margin: spacing(3, 3),
      [breakpoints.down("xs")]: {
        margin: spacing(3, 0, 0, 0)
      }
    },
    container: {
      display: "grid",
      gridGap: "48px",
      gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
      flexWrap: "wrap",
      [breakpoints.down("sm")]: {
        gridGap: "12px"
      },
      [breakpoints.down("xs")]: {
        gridGap: "12px",
        gridTemplateColumns: "repeat(auto-fit, minmax(100%, 1fr))"
      }
    },
    banner: {
      padding: spacing(2),
      width: "60%",
      backgroundColor: palette.info.light,
      border: "thin solid",
      borderColor: palette.divider,
      borderRadius: shape.borderRadius,
      textAlign: "center",
      color: palette.info.contrastText,
    },
    newEvalButton: {
      position: "absolute",
      right: spacing(3),
    },

    main: {
      width: "100%"
    },
    card: {
      display: "flex",
      flexDirection: "column"
    },
    cardHeader: {
      color: palette.background.paper,
      backgroundColor: palette.primary.main,
      padding: spacing(2, 4, 2, 4)
    },
    cardContents: {
      padding: 0,
      display: "flex",
      flexGrow: 1,
      height: "300px",
      flexDirection: "column",
      overflow: "hidden",
    },
    cardFooter: {
      backgroundColor: palette.grey[200],
      display: "flex",
      justifyContent: "flex-end",
    },
    link: {
      textDecoration: "none",
    },
    fabNewEvalButton: {
      position: "absolute",
      bottom: spacing(2),
      right: spacing(2),
    },
    loading: {
      backgroundColor: '#fff',
    },

    facilitiesRoot: {
      // padding: spacing(1,0,0),
      marginTop: spacing(-3),
      marginBottom: spacing(-3),
      overflowX: 'hidden'
    },
    facilitiesList: {
      margin: spacing(0, -3),
      boxShadow: shadows[0],
    },
  })
);

export default Home;
